/**
 * Pictures kept in this browser, for practice without a teacher account.
 *
 * A signed-in teacher's pictures live on the server. Someone practising
 * without an account still deserves a picture that survives a refresh, so
 * theirs go into IndexedDB — the one browser store that holds image bytes
 * comfortably. They never leave the device, which is also why a live class
 * cannot use them: the children's tablets have no way to reach them.
 *
 * The limits mirror the server's, so a picture accepted here would also be
 * accepted after signing in.
 */

export const LOCAL_PREFIX = 'local:';
export const LOCAL_MAX_BYTES = 4 * 1024 * 1024;
export const LOCAL_MAX_COUNT = 40;
const MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export interface LocalPicture {
  id: string;
  name: string;
  mime: string;
  bytes: number;
  createdAt: number;
  blob: Blob;
}

/** Why a picture was refused — the same codes the server sends. */
export class LocalPictureError extends Error {
  constructor(readonly code: 'bad_type' | 'too_large' | 'too_many' | 'generic') {
    super(code);
  }
}

const DB_NAME = 'numslearn-media';
const STORE = 'pictures';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (opening) return opening;
  opening = new Promise((resolve, reject) => {
    // Absent in some private windows, and throws outright in others.
    if (typeof indexedDB === 'undefined') { reject(new Error('no indexedDB')); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  // A failed open must not be cached: the next call should try again.
  opening.catch(() => { opening = null; });
  return opening;
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}

/** Can this browser keep pictures at all? */
export async function localPicturesAvailable(): Promise<boolean> {
  try { await open(); return true; } catch { return false; }
}

/** Newest first, as the server lists them. */
export async function listLocalPictures(): Promise<LocalPicture[]> {
  const rows = await run<LocalPicture[]>('readonly', (s) => s.getAll() as IDBRequest<LocalPicture[]>);
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export async function addLocalPicture(file: File, mime: string): Promise<LocalPicture> {
  if (!MIMES.includes(mime)) throw new LocalPictureError('bad_type');
  if (file.size === 0 || file.size > LOCAL_MAX_BYTES) throw new LocalPictureError('too_large');
  const existing = await listLocalPictures();
  if (existing.length >= LOCAL_MAX_COUNT) throw new LocalPictureError('too_many');

  const row: LocalPicture = {
    // Not crypto.randomUUID: it exists only on HTTPS and localhost, and a
    // classroom laptop may well reach the app over plain http on the LAN.
    id: `${LOCAL_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`,
    name: file.name.slice(0, 120),
    mime,
    bytes: file.size,
    createdAt: Date.now(),
    // Stored with its real type, so it comes back as an image and not as
    // anonymous bytes the <img> has to sniff.
    blob: new Blob([await file.arrayBuffer()], { type: mime }),
  };
  await run('readwrite', (s) => s.put(row));
  return row;
}

export async function removeLocalPicture(id: string): Promise<void> {
  await run('readwrite', (s) => s.delete(id));
}

export const isLocalPicture = (id: string): boolean => id.startsWith(LOCAL_PREFIX);
